import { AfterViewInit, Component, OnInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  PromiseToPayConfigEmployee,
  PromiseToPayConfigService,
  PromiseToPayPermissionLists,
} from '@lmc-app/core/services/promise-to-pay-config.service';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';

type PermissionKey = keyof PromiseToPayPermissionLists;

interface PermissionColumn {
  key: PermissionKey;
  label: string;
  hint: string;
}

const PERMISSION_COLUMNS: PermissionColumn[] = [
  { key: 'changeOwner', label: 'Change Owner', hint: 'Change the owner of a requested promise' },
  { key: 'exceedCap', label: 'Exceed Cap', hint: 'Create a PTP above the total current due' },
  { key: 'backdate', label: 'Backdate', hint: 'Pick a past date for any promise or arrangement' },
  { key: 'edit', label: 'Edit', hint: 'Edit any promise or arrangement, whatever its payment method or status' },
];

@Component({
  selector: 'app-promise-to-pay-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
  ],
  templateUrl: './promise-to-pay-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./promise-to-pay-settings.component.scss'],
})
export class PromiseToPaySettingsComponent implements OnInit, AfterViewInit {
  private readonly _PromiseToPayConfigService = inject(PromiseToPayConfigService);
  private readonly _SnackBar = inject(MatSnackBar);

  @ViewChild(MatSort) sort: MatSort;

  readonly permissionColumns = PERMISSION_COLUMNS;
  readonly displayedColumns: string[] = ['full_name', 'position', ...PERMISSION_COLUMNS.map((c) => c.key)];
  dataSource = new MatTableDataSource<PromiseToPayConfigEmployee>([]);

  selected: Record<PermissionKey, Set<number>> = {
    changeOwner: new Set(),
    exceedCap: new Set(),
    backdate: new Set(),
    edit: new Set(),
  };
  searchText = '';
  selectedOnly = false;
  isLoading = false;
  isSaving = false;

  ngOnInit(): void {
    this.dataSource.filterPredicate = (employee, filter) => {
      if (this.selectedOnly && !this.hasAny(employee)) return false;
      const search = filter.trim();
      return [employee.full_name, employee.position]
        .some((value) => (value || '').toLowerCase().includes(search));
    };
    this.loadConfig();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  loadConfig(): void {
    this.isLoading = true;
    this._PromiseToPayConfigService.getConfig().subscribe({
      next: (res) => {
        const { permissions } = res.data;
        this.selected = {
          changeOwner: new Set(permissions.changeOwner),
          exceedCap: new Set(permissions.exceedCap),
          backdate: new Set(permissions.backdate),
          edit: new Set(permissions.edit),
        };
        this.dataSource.data = res.data.employees;
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.showMessage(error.error?.message || 'Failed to load Promise to Pay permissions');
      },
    });
  }

  applyFilter(): void {
    // A non-empty filter string is needed for the predicate to run
    this.dataSource.filter = this.searchText.trim().toLowerCase() || ' ';
  }

  isSelected(employee: PromiseToPayConfigEmployee, key: PermissionKey): boolean {
    return this.selected[key].has(employee.employeeID);
  }

  toggle(employee: PromiseToPayConfigEmployee, key: PermissionKey): void {
    const ids = this.selected[key];
    if (ids.has(employee.employeeID)) {
      ids.delete(employee.employeeID);
    } else {
      ids.add(employee.employeeID);
    }
  }

  count(key: PermissionKey): number {
    return this.selected[key].size;
  }

  save(): void {
    this.isSaving = true;
    const permissions: PromiseToPayPermissionLists = {
      changeOwner: [...this.selected.changeOwner],
      exceedCap: [...this.selected.exceedCap],
      backdate: [...this.selected.backdate],
      edit: [...this.selected.edit],
    };
    this._PromiseToPayConfigService.updateConfig(permissions).subscribe({
      next: (res) => {
        this.isSaving = false;
        this.showMessage(res.message);
      },
      error: (error) => {
        this.isSaving = false;
        this.showMessage(error.error?.message || 'Failed to save Promise to Pay permissions');
      },
    });
  }

  private hasAny(employee: PromiseToPayConfigEmployee): boolean {
    return PERMISSION_COLUMNS.some((column) => this.isSelected(employee, column.key));
  }

  private showMessage(message: string): void {
    this._SnackBar.open(message, SnackbarConstants.ACTION_DISMISS, SnackbarConstants.DURATION_DISMISS_SHORT);
  }
}
